import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Instagram, Facebook } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CrownMark } from "../components/CrownMark";
import { CrownWatermark } from "../components/CrownWatermark";
import { PostBadge } from "../components/PostBadge";
import { BLOG_POSTS } from "../data/blog";
import { IMAGES } from "../data/content";
import { INSTAGRAM_HANDLE, INSTAGRAM_URL, FACEBOOK_URL } from "../data/contact";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

const DOMAIN = "https://www.speedyresurfacing.com.au";

export default function Blog() {
  const [feature, ...rest] = BLOG_POSTS;
  const royal = feature.theme === "royal";

  return (
    <div className="min-h-screen noise pt-28 pb-24" data-testid="blog-index">
      <Seo
        title="Blog Articles | Speedy Resurfacing Sydney"
        description="Expert articles on court resurfacing, custom court design, accessible parking line marking, SDA external environments and precision surfacing in Sydney & NSW — written by the Speedy Resurfacing crew."
        path="/blog-articles"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Blog",
              name: "Speedy Resurfacing Blog Articles",
              url: DOMAIN + "/blog-articles",
              description: "Court resurfacing, custom court design, accessible line marking and SDA external environment insight from Sydney's surfacing specialists.",
              blogPost: BLOG_POSTS.map((p) => ({
                "@type": "BlogPosting",
                headline: p.title,
                description: p.dek,
                datePublished: p.date,
                url: `${DOMAIN}/blog-articles/${p.slug}`,
                author: { "@type": "Organization", name: "Speedy Resurfacing" },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
                { "@type": "ListItem", position: 2, name: "Blog Articles", item: DOMAIN + "/blog-articles" },
              ],
            },
          ],
        }}
      />

      <div className="mx-auto max-w-7xl px-5 md:px-10">
        {/* Masthead — Acknowledgement of Country */}
        <Reveal>
          <div className="relative overflow-hidden border-y-2 border-gold/60 py-8 md:py-10" data-testid="blog-acknowledgement">
            <CrownWatermark className="right-[-14%] top-1/2 w-[70vw] max-w-none -translate-y-1/2 lg:right-[-3%] lg:w-[34vw] lg:max-w-[620px]" testid="blog-hub-crown-watermark" />
            <h1 className="sr-only">Blog Articles</h1>
            <div className="relative flex items-center gap-4">
              <CrownMark className="h-9 md:h-11" />
              <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">Acknowledgement of Country</span>
            </div>
            <p className="relative mt-5 max-w-5xl text-base md:text-lg leading-relaxed text-zinc-200">
              Speedy Resurfacing and its parent division, Speedy Line Marking, acknowledge the Traditional Custodians of the
              lands across the Sydney basin. We pay our deepest respects to the Gadigal people of the Eora Nation, as well as
              Elders past, present, and emerging. We honour their unbroken connection to the ancient land, waterways, and
              community. Just as Sydney is built upon the enduring foundation of Hawkesbury Sandstone, we commit to building
              accessible infrastructure and inclusive environments with integrity, longevity, and profound respect for the
              sacred ground upon which our communities live, move, and thrive together.
            </p>
          </div>
        </Reveal>

        {/* Feature — asymmetric overlap */}
        <Reveal delay={0.08}>
          <article className="relative mt-16 lg:grid lg:grid-cols-12 lg:items-center" data-testid={`blog-feature-${feature.slug}`}>
            <Link to={`/blog-articles/${feature.slug}`} className="block lg:col-span-7">
              <div className="relative overflow-hidden rounded-lg border border-white/10">
                <img {...responsive(IMAGES[feature.hero], "(min-width: 1024px) 58vw, 100vw", [480, 800, 1200])} decoding="async" alt={feature.title}
                     className="h-[280px] w-full object-cover md:h-[480px] transition-transform duration-[1.2s] hover:scale-105" />
                <div className={`absolute inset-0 bg-gradient-to-t ${royal ? "from-royal-deep/70" : "from-obsidian/70"} via-transparent to-transparent`} />
                <PostBadge post={feature} className="absolute left-5 top-5" />
              </div>
            </Link>

            <div className="relative z-10 lg:col-span-5 lg:-ml-16">
              <div className={`-mt-10 mx-4 rounded-lg border bg-obsidian-800/95 p-7 backdrop-blur md:p-10 lg:mt-0 lg:mx-0 ${
                royal ? "border-royal/50 shadow-[0_0_90px_rgba(31,79,216,0.22)]" : "border-gold/25"
              }`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  {feature.dateLabel} · {feature.readTime}
                </span>
                <h2 className="mt-4 font-heading font-black text-2xl md:text-3xl leading-[1.1] tracking-tight">
                  <Link to={`/blog-articles/${feature.slug}`} className="hover:text-neon transition-colors">{feature.title}</Link>
                </h2>
                <p className="mt-4 text-base text-zinc-300">{feature.dek}</p>
                <Link to={`/blog-articles/${feature.slug}`} data-testid="blog-feature-read"
                      className="group mt-7 inline-flex items-center gap-2 font-bold text-gold hover:text-gold-hover">
                  Read the article
                  <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          </article>
        </Reveal>

        {/* Additional posts as editorial rows */}
        {rest.length > 0 && (
          <div className="mt-20 border-t border-white/10">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link to={`/blog-articles/${p.slug}`} data-testid={`blog-row-${p.slug}`}
                      className="group grid grid-cols-[auto_1fr_auto] items-center gap-6 border-b border-white/10 py-8 px-2 hover:bg-white/[0.02] transition-colors">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{p.dateLabel}</span>
                  <div>
                    <PostBadge post={p} className="mb-3" />
                    <h3 className="font-heading font-bold text-xl md:text-2xl group-hover:text-neon transition-colors">{p.title}</h3>
                    <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{p.dek}</p>
                  </div>
                  <ArrowUpRight className="text-zinc-700 group-hover:text-gold transition-colors" size={24} />
                </Link>
              </Reveal>
            ))}
          </div>
        )}

        {/* Topics */}
        <Reveal>
          <div className="mt-20">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">What we write about</span>
            <div className="mt-5 flex flex-wrap gap-2">
              {feature.tags.map((t) => (
                <span key={t} className="rounded-sm border border-white/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">{t}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* CTA band */}
        <Reveal delay={0.06}>
          <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-lg border border-neon/20 bg-obsidian-800 p-8 md:p-12">
            <div>
              <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight">GOT A COURT IN MIND?</h2>
              <p className="mt-2 text-zinc-300">Leave your name, mobile and email — we'll call to arrange a site visit and come back with an obligation-free proposal.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/assessment" data-testid="blog-cta-assessment"
                    className="group inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian shadow-neon hover:bg-neon-hover transition-colors">
                Request a Site Assessment
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={INSTAGRAM_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-instagram-follow"
                 className="inline-flex items-center gap-2 rounded-sm border border-gold/40 px-6 py-4 font-bold text-gold hover:bg-gold/10 hover:border-gold transition-colors">
                <Instagram size={18} /> Follow {INSTAGRAM_HANDLE}
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="me noopener noreferrer" data-testid="blog-facebook-follow"
                 className="inline-flex items-center gap-2 rounded-sm border border-gold/40 px-6 py-4 font-bold text-gold hover:bg-gold/10 hover:border-gold transition-colors">
                <Facebook size={18} /> Follow us on Facebook
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
